import { useContext, useMemo } from 'react';
import {
  EditRegular,
  EditOffRegular,
  CheckmarkFilled,
  PeopleRegular,
  RenameRegular,
  EyeFilled,
  DocumentSearchRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { useFolder } from '@modules/items';
import { AccessType, TPermissions, useCanEdit } from '@modules/access';
import { useSearchParams } from '@shared/hooks';
import { openInNewTab } from '@shared/helpers';
import { TOverflowButtonParam } from '@shared/components';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '../types';
import { SignInContext, USER_GROUPS } from '../../users';
import { useNavigate, useParams } from 'react-router-dom';

type TProps = {
  inlineBlock: TInlineBlock;
  version: TInlineBlockDraft | TInlineBlockVersion | undefined;
  permissions: TPermissions;
  isEdit: boolean;
  handleSave: () => void;
  handleEditDraft: () => void;
  handleComplete: () => void;
  handleShowAccess: () => void;
  handleOpenSettings: () => void;
  layoutState: any;
};

const useInlineBlockNavActions = ({
  inlineBlock,
  version,
  permissions,
  isEdit,
  handleSave,
  handleEditDraft,
  handleComplete,
  handleShowAccess,
  handleOpenSettings,
  layoutState,
}: TProps): TOverflowButtonParam[] => {
  const { user } = useContext(SignInContext);
  const { canEdit: canEditWithoutAuthorGroup } = useCanEdit(permissions.accessRight, { authorGroup: false });
  const { change } = useSearchParams();
  const draft = inlineBlock.draft;
  const latest = draft ?? inlineBlock.versions[0];
  const isLatestView = !version || version === latest;

  const productId = inlineBlock.resolvedPath[0]._id;
  const isAdmin = user.groups.includes(USER_GROUPS.ADMIN);

  return useMemo(() => {
    const actions: TOverflowButtonParam[] = [];

    if (isLatestView) {
      // Create / Edit draft
      if (permissions.accessRight >= AccessType.EDIT) {
        if (isEdit) {
          actions.push({
            appearance: 'primary',
            children: 'Stop editing',
            icon: <EditOffRegular />,
            onClick: handleSave,
          });
        } else {
          actions.push({
            appearance: 'primary',
            children: draft ? 'Edit draft' : 'Create draft',
            icon: <EditRegular />,
            onClick: handleEditDraft,
            // disabled: false
          });
        }
      }
    } else {
      // View latest version
      actions.push({
        children: `View ${draft ? 'draft' : 'latest'}`,
        icon: <EyeFilled />,
        onClick: () => change('view', 'latest'),
      });
    }

    // Complete version
    if (permissions.accessRight >= AccessType.EDIT && draft) {
      actions.push({
        children: 'Complete version',
        icon: <CheckmarkFilled />,
        onClick: handleComplete,
        // disabled: false,
      });
    }

    // Manage access
    if (permissions.accessRight >= AccessType.MANAGE) {
      actions.push({
        children: 'Manage access',
        icon: <PeopleRegular />,
        onClick: handleShowAccess,
      });
    }

    // Rename
    if (layoutState) {
      actions.push({
        children: 'Rename',
        icon: <RenameRegular />,
        onClick: layoutState.onRename,
        disabled: !canEditWithoutAuthorGroup,
      });
    }

    // Library detection
    actions.push({
      children: 'Detect matches',
      icon: <DocumentSearchRegular />,
      onClick: () => {
        // User-initiated click, so the browser does not block the new tab.
        openInNewTab(buildInlineBlockDetectionUrl(productId, inlineBlock._id));
      },
      disabled: !isAdmin, // TODO: hide or disable?
    });

    // Settings dialog (URL controls + tag selector) - last in the list
    if (permissions.accessRight >= AccessType.EDIT) {
      actions.push({
        children: 'Settings',
        icon: <SettingsRegular />,
        onClick: handleOpenSettings,
      });
    }

    return actions;
  }, [draft, isLatestView, productId, inlineBlock._id, isEdit, permissions, layoutState, version, handleOpenSettings]);
};

const buildInlineBlockDetectionUrl = (productId: string, inlineBlockId: string) => {
  const params = new URLSearchParams();
  params.set('preselectedInlineBlock', inlineBlockId);
  return `/product/${productId}/library-detection?${params.toString()}`;
};

export default useInlineBlockNavActions;
